import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';

const ParkingSlotForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        siteId: "",
        wingId: "",
        floorId: "",
        slotName: "",
        vehicleTypeId: "",
        parkingTypeId: "",
    });

    const [siteList, setSiteList] = useState([]);
    const [wingList, setWingList] = useState([]);
    const [floorList, setFloorList] = useState([]);
    const [vehicleTypeList, setVehicleTypeList] = useState([]);
    const [parkingTypeList, setParkingTypeList] = useState([]);

    const isEdit = !!location.state?.recordData;
    const recordData = location.state?.recordData;

    useEffect(() => {
        if (isEdit && recordData) {
            setFormData({
                siteId: recordData.siteId || "",
                wingId: recordData.wingId || "",
                floorId: recordData.floorId || "",
                slotName: recordData.slotName || "",
                vehicleTypeId: recordData.vehicleTypeId || "",
                parkingTypeId: recordData.parkingTypeId || "",
            });
            if (recordData.siteId) fetchWings(recordData.siteId);
            if (recordData.wingId) fetchFloors(recordData.wingId);
        }
        fetchDropdownData();
    }, [isEdit, recordData]);

    const extractArray = (apiData, fallbackKey) => {
        if (!apiData) return [];
        if (Array.isArray(apiData)) return apiData;
        if (apiData.data && Array.isArray(apiData.data)) return apiData.data;
        if (fallbackKey && apiData[fallbackKey] && Array.isArray(apiData[fallbackKey])) return apiData[fallbackKey];
        return [];
    };

    const fetchDropdownData = async () => {
        try {
            const siteRes = await apiServices({ method: "GET", url: `/SiteDetailsMaster/GetAllSiteDetails` });
            if (siteRes.isSuccess || siteRes.success || siteRes.IsSuccess) {
                setSiteList(extractArray(siteRes.data, 'SiteDetailsData').map(s => ({ label: s.siteName, value: s.siteId })));
            }
            
            const vehicleRes = await apiServices({ method: "GET", url: `/VehicleTypeMaster/getAllVehicleType` });
            if (vehicleRes.isSuccess || vehicleRes.success || vehicleRes.IsSuccess) {
                setVehicleTypeList(extractArray(vehicleRes.data, 'VehicleTypeData').map(v => ({ label: v.VehicleTypename, value: v.VehicleTypeid })));
            }
            
            const parkingRes = await apiServices({ method: "GET", url: `/ParkingTypeMaster/GetAllParkingTypes` });
            if (parkingRes.isSuccess || parkingRes.success || parkingRes.IsSuccess) {
                setParkingTypeList(extractArray(parkingRes.data, 'ParkingTypeData').map(p => ({ label: p.parkingType, value: p.parkingTypeID })));
            }
        } catch (error) {
            console.error("Error fetching dropdowns:", error);
            toast.error("Failed to load form options");
        }
    };

    const fetchWings = async (siteId) => {
        try {
            const res = await apiServices({ method: "GET", url: `/WingMaster/GetAllWing` });
            if (res.isSuccess || res.success || res.IsSuccess) {
                const wings = extractArray(res.data, 'WingMasterData');
                setWingList(wings.filter(item => item.SiteId === parseInt(siteId)).map(w => ({ label: w.WingName, value: w.WingId })));
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchFloors = async (wingId) => {
        try {
            const res = await apiServices({ method: "GET", url: `/FloorMaster/GetAllFloor` });
            if (res.isSuccess || res.success || res.IsSuccess) {
                const floors = extractArray(res.data, 'FloorMasterData');
                setFloorList(floors.filter(item => item.WingId === parseInt(wingId)).map(f => ({ label: f.FloorName, value: f.FloorId })));
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleSiteChange = (opt) => {
        const siteId = opt ? opt.value : "";
        setFormData({ ...formData, siteId, wingId: "", floorId: "" });
        setWingList([]);
        setFloorList([]);
        if (siteId) fetchWings(siteId);
    };

    const handleWingChange = (opt) => {
        const wingId = opt ? opt.value : "";
        setFormData({ ...formData, wingId, floorId: "" });
        setFloorList([]);
        if (wingId) fetchFloors(wingId);
    };

    const validateForm = () => {
        if (!formData.siteId) return toast.warning("Site is required"), false;
        if (!formData.wingId) return toast.warning("Wing is required"), false;
        if (!formData.floorId) return toast.warning("Floor is required"), false;
        if (!formData.vehicleTypeId) return toast.warning("Vehicle Type is required"), false;
        if (!formData.parkingTypeId) return toast.warning("Parking Type is required"), false;
        if (!formData.slotName.trim()) return toast.warning("Slot Name is required"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            slotName: formData.slotName,
            siteId: parseInt(formData.siteId),
            wingId: parseInt(formData.wingId),
            floorId: parseInt(formData.floorId),
            parkingTypeId: parseInt(formData.parkingTypeId),
            vehicleTypeId: parseInt(formData.vehicleTypeId),
            createdBy: "sys"
        };

        try {
            let res;
            if (!isEdit) {
                res = await apiServices({
                    method: "POST",
                    url: `/ParkingSlotMaster/postParkingSlotMaster`,
                    data: payload
                });
            } else {
                // Assuming PUT for edit (though original modal didn't seem to implement edit fully for Parking Slot, applying standard pattern)
                res = await apiServices({
                    method: "PUT",
                    url: `/ParkingSlotMaster/putParkingSlotMaster/${recordData.parkingSlotId}`,
                    data: payload
                });
            }

            if (res.isSuccess || res.success || res.IsSuccess) {
                toast.success(`Parking slot ${isEdit ? "updated" : "added"} successfully`);
                navigate('/zentcomm/parking-management/parking-slot');
            } else {
                toast.error(res.message || `Failed to ${isEdit ? 'update' : 'create'} parking slot`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <PageHeadingSection 
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Parking Slot`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} permanent parking slot configuration.`}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <CustomSelect 
                    label={<>Site <span className="text-red-500">*</span></>}
                    options={siteList}
                    value={siteList.find(s => s.value === formData.siteId) || null}
                    onChange={handleSiteChange}
                />
                
                <CustomSelect 
                    label={<>Wing <span className="text-red-500">*</span></>}
                    options={wingList}
                    value={wingList.find(w => w.value === formData.wingId) || null}
                    onChange={handleWingChange}
                    isDisabled={!formData.siteId}
                />
                
                <CustomSelect 
                    label={<>Floor <span className="text-red-500">*</span></>}
                    options={floorList}
                    value={floorList.find(f => f.value === formData.floorId) || null}
                    onChange={(opt) => setFormData({ ...formData, floorId: opt ? opt.value : "" })}
                    isDisabled={!formData.wingId}
                />
                
                <CustomSelect 
                    label={<>Vehicle Type <span className="text-red-500">*</span></>}
                    options={vehicleTypeList}
                    value={vehicleTypeList.find(v => v.value === formData.vehicleTypeId) || null}
                    onChange={(opt) => setFormData({ ...formData, vehicleTypeId: opt ? opt.value : "" })}
                />
                
                <CustomSelect 
                    label={<>Parking Type <span className="text-red-500">*</span></>}
                    options={parkingTypeList}
                    value={parkingTypeList.find(p => p.value === formData.parkingTypeId) || null}
                    onChange={(opt) => setFormData({ ...formData, parkingTypeId: opt ? opt.value : "" })}
                />
                
                <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="slotName">
                        Slot Name <span className="text-red-500">*</span>
                    </label>
                    <input 
                        className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:border-indigo-500 transition-colors" 
                        type="text" 
                        id="slotName" 
                        value={formData.slotName} 
                        onChange={(e) => setFormData({ ...formData, slotName: e.target.value })} 
                        placeholder="Enter Slot Name (e.g. A-101)"
                    />
                </div>
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentcomm/parking-management/parking-slot")}
                    className="px-3 py-2 border rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default ParkingSlotForm;
